import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Zap, ChevronRight, CheckCircle2, XCircle, RotateCcw,
  BookOpen, FileText, ArrowLeft, Clock, AlertTriangle,
} from 'lucide-react';
import { getStoredSession } from '../hooks/useAuth';
import ChinniExplainer from '../components/ChinniExplainer';
import {
  isDrillEnabled, requestDrillSet, fetchDrillSetQuestions, pollDrillSetReady,
  startDrillSession, completeDrillSession, abandonDrillSession,
  getWeakConcepts, getDrillSubjects, getDrillConcepts,
  type DrillQuestion, type SessionResult, type WeakConcept,
  type DrillSubject, type DrillConcept,
} from '../services/drillService';

// ── Types ──────────────────────────────────────────────────────
type Phase = 'setup' | 'generating' | 'session' | 'results';
type SourceType = 'ai_generated' | 'paste';
type DrillMode = 'rapid_fire' | 'relax';
type Difficulty = 'easy' | 'medium' | 'hard';
type QuestionCount = 10 | 20 | 60;

const OPTION_LABELS = ['A', 'B', 'C', 'D'];
const formatTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
};

// ── Component ──────────────────────────────────────────────────
export default function DrillPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = getStoredSession();

  const grade = session?.grade_level ?? 0;
  const board = session?.board ?? '';
  const studentId = session?.student_id ?? '';

  const retakeState = (location.state as {
    retakeSetId?: string;
    conceptId?: number;
    conceptName?: string;
    difficulty?: Difficulty;
    sourceType?: SourceType;
  } | null);

  // Grade gate
  useEffect(() => {
    if (session && !isDrillEnabled(grade, board)) navigate('/dashboard', { replace: true });
  }, [session, grade, board, navigate]);

  // ── Setup state ──────────────────────────────────────────────
  const [sourceType, setSourceType] = useState<SourceType>(retakeState?.sourceType ?? 'ai_generated');
  const [subjects, setSubjects] = useState<DrillSubject[]>([]);
  const [concepts, setConcepts] = useState<DrillConcept[]>([]);
  const [weakConcepts, setWeakConcepts] = useState<WeakConcept[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [selectedConceptId, setSelectedConceptId] = useState<number | null>(retakeState?.conceptId ?? null);
  const [selectedConceptName, setSelectedConceptName] = useState(retakeState?.conceptName ?? '');
  const [selectedSubjectName, setSelectedSubjectName] = useState('');
  const [pasteContent, setPasteContent] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>(retakeState?.difficulty ?? 'medium');
  const [questionCount, setQuestionCount] = useState<QuestionCount>(20);
  const [mode, setMode] = useState<DrillMode>('relax');
  const [timerMinutes, setTimerMinutes] = useState<2 | 5>(5);
  const [setupError, setSetupError] = useState('');
  const [generatingError, setGeneratingError] = useState('');

  // ── Session state ────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('setup');
  const [questions, setQuestions] = useState<DrillQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<SessionResult[]>([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTimeLeft, setTotalTimeLeft] = useState(0);

  const questionsRef = useRef<DrillQuestion[]>([]);
  const currentIndexRef = useRef(0);
  const resultsRef = useRef<SessionResult[]>([]);
  const sessionIdRef = useRef('');
  const setIdRef = useRef('');
  const studentIdRef = useRef('');
  const phaseRef = useRef<Phase>('setup');

  useEffect(() => { studentIdRef.current = studentId; }, [studentId]);

  useEffect(() => {
    if (retakeState?.retakeSetId) setIdRef.current = retakeState.retakeSetId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { questionsRef.current = questions; }, [questions]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ── Load setup data ──────────────────────────────────────────
  useEffect(() => {
    if (!studentId) return;
    getDrillSubjects().then(setSubjects);
    getWeakConcepts(studentId, 3).then(setWeakConcepts);
  }, [studentId]);

  useEffect(() => {
    if (!selectedSubjectId) { setConcepts([]); return; }
    getDrillConcepts(selectedSubjectId).then(setConcepts);
  }, [selectedSubjectId]);

  // ── Abandon on unmount ───────────────────────────────────────
  useEffect(() => {
    return () => {
      if (phaseRef.current === 'session' && sessionIdRef.current) {
        abandonDrillSession(sessionIdRef.current, studentIdRef.current);
      }
    };
  }, []);

  // ── Session end ──────────────────────────────────────────────
  const endSession = useCallback(async (finalResults: SessionResult[]) => {
    setPhase('results');
    phaseRef.current = 'results';
    if (sessionIdRef.current) await completeDrillSession(sessionIdRef.current, studentIdRef.current, finalResults);
  }, []);

  // ── Advance ──────────────────────────────────────────────────
  const handleAdvance = useCallback((answer: number | null, timedOut = false) => {
    const q = questionsRef.current[currentIndexRef.current];
    if (!q) return;
    const result: SessionResult = {
      question_index: currentIndexRef.current,
      student_answer: answer,
      correct: answer !== null && answer === q.correct_option_index,
      timed_out: timedOut,
      skill_id: q.skill_id,
    };
    const newResults = [...resultsRef.current, result];
    resultsRef.current = newResults;
    setResults([...newResults]);
    if (currentIndexRef.current + 1 >= questionsRef.current.length) {
      endSession(newResults);
    } else {
      currentIndexRef.current += 1;
      setCurrentIndex(currentIndexRef.current);
    }
  }, [endSession]);

  // ── Rapid-fire timer ─────────────────────────────────────────
  const perQuestionMs = Math.floor((timerMinutes * 60000) / questionCount);

  useEffect(() => {
    if (phase !== 'session' || mode !== 'rapid_fire') return;
    setTimeLeft(perQuestionMs);
  }, [currentIndex, phase, mode, perQuestionMs]);

  useEffect(() => {
    if (phase !== 'session' || mode !== 'rapid_fire' || timeLeft <= 0) return;
    const id = setInterval(() => setTimeLeft(p => Math.max(0, p - 100)), 100);
    return () => clearInterval(id);
  }, [phase, mode, timeLeft]);

  useEffect(() => {
    if (phase !== 'session' || mode !== 'rapid_fire' || timeLeft > 0) return;
    handleAdvance(null, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // ── Relax timer ──────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'session' || mode !== 'relax') return;
    setTotalTimeLeft(timerMinutes * 60000);
    const id = setInterval(() => setTotalTimeLeft(p => Math.max(0, p - 1000)), 1000);
    return () => clearInterval(id);
  }, [phase, mode, timerMinutes]);

  useEffect(() => {
    if (phase !== 'session' || mode !== 'relax' || totalTimeLeft > 0) return;
    endSession(resultsRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTimeLeft]);

  // ── Start ────────────────────────────────────────────────────
  async function handleStart() {
    setSetupError('');
    if (sourceType === 'ai_generated' && !selectedConceptId) { setSetupError('Please select a concept.'); return; }
    if (sourceType === 'paste' && pasteContent.trim().length < 100) { setSetupError('Please paste at least 100 characters.'); return; }

    setPhase('generating');
    setGeneratingError('');

    if (setIdRef.current) {
      const drillQuestions = await fetchDrillSetQuestions(setIdRef.current);
      if (!drillQuestions) { setGeneratingError('Could not load questions. Please try again.'); setPhase('setup'); return; }
      const sliced = drillQuestions.slice(0, questionCount);
      questionsRef.current = sliced;
      setQuestions(sliced);
      const sid = await startDrillSession(studentId, setIdRef.current, mode, timerMinutes, questionCount);
      if (!sid) { setGeneratingError('Failed to start session.'); setPhase('setup'); return; }
      sessionIdRef.current = sid;
      currentIndexRef.current = 0;
      resultsRef.current = [];
      setCurrentIndex(0);
      setResults([]);
      setPhase('session');
      phaseRef.current = 'session';
      return;
    }

    const params = sourceType === 'ai_generated'
      ? { source_type: 'ai_generated', concept_id: selectedConceptId, concept_name: selectedConceptName, grade, board, subject: selectedSubjectName, difficulty }
      : { source_type: 'paste', paste_content: pasteContent, grade, difficulty };

    const resp = await requestDrillSet(params);
    if (!resp || resp.status === 'failed') { setGeneratingError('Could not start drill. Please try again.'); setPhase('setup'); return; }

    const drillQuestions = resp.status === 'ready'
      ? await fetchDrillSetQuestions(resp.set_id)
      : await pollDrillSetReady(resp.set_id);

    if (!drillQuestions) { setGeneratingError('Failed to generate questions. Please try again.'); setPhase('setup'); return; }

    const sliced = drillQuestions.slice(0, questionCount);
    questionsRef.current = sliced;
    setQuestions(sliced);
    setIdRef.current = resp.set_id;

    const sid = await startDrillSession(studentId, resp.set_id, mode, timerMinutes, questionCount);
    if (!sid) { setGeneratingError('Failed to start session. Please try again.'); setPhase('setup'); return; }

    sessionIdRef.current = sid;
    currentIndexRef.current = 0;
    resultsRef.current = [];
    setCurrentIndex(0);
    setResults([]);
    setPhase('session');
    phaseRef.current = 'session';
  }

  function handleRetake() {
    currentIndexRef.current = 0;
    resultsRef.current = [];
    setCurrentIndex(0);
    setResults([]);
    (async () => {
      const sid = await startDrillSession(studentId, setIdRef.current, mode, timerMinutes, questionCount);
      if (sid) sessionIdRef.current = sid;
      setPhase('session');
      phaseRef.current = 'session';
    })();
  }

  // ── Results helpers ──────────────────────────────────────────
  const skillStats = results.reduce((acc, r) => {
    if (!acc[r.skill_id]) acc[r.skill_id] = { correct: 0, total: 0 };
    acc[r.skill_id].total++;
    if (r.correct) acc[r.skill_id].correct++;
    return acc;
  }, {} as Record<string, { correct: number; total: number }>);

  const strengthened = Object.entries(skillStats).filter(([, s]) => s.correct / s.total >= 0.7).map(([k]) => k.replace(/_/g, ' '));
  const stillWeak = Object.entries(skillStats).filter(([, s]) => s.correct / s.total < 0.7).map(([k]) => k.replace(/_/g, ' '));

  const attempted = results.length;
  const correct = results.filter(r => r.correct).length;
  const pct = attempted > 0 ? Math.round((correct / attempted) * 100) : 0;

  const currentQ = questions[currentIndex];

  // Rapid-fire ring
  const RING_R = 20, RING_C = 2 * Math.PI * RING_R;
  const ringProgress = perQuestionMs > 0 ? timeLeft / perQuestionMs : 0;
  const ringOffset = RING_C * (1 - ringProgress);
  const ringColor = ringProgress > 0.4 ? '#6366f1' : '#ef4444';

  // ─────────────────────────────────────────────────────────────
  // SETUP
  // ─────────────────────────────────────────────────────────────
  if (phase === 'setup') return (
    <div className="pb-12 min-h-screen">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl shadow-lg shadow-indigo-500/30">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-ocean-deep">Drill</h1>
            <p className="text-sm text-gray-500">Rapid reinforcement for exam prep</p>
          </div>
          <Link to="/drill-archive" className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-coral-warm transition-colors font-medium">
            <BookOpen className="w-3.5 h-3.5" /> History
          </Link>
        </div>

        {retakeState?.retakeSetId && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-orange-50 border border-orange-200 mb-5 text-sm text-orange-800">
            <RotateCcw className="w-4 h-4 shrink-0" />
            <span>Retaking <strong>{retakeState.conceptName ?? 'previous drill'}</strong> — same questions, adjust settings below</span>
          </div>
        )}

        {/* Source toggle */}
        <div className="glass-panel rounded-2xl p-1 flex mb-6">
          {([['ai_generated', 'AI-generated', BookOpen], ['paste', 'My Notes', FileText]] as [SourceType, string, typeof BookOpen][]).map(([st, label, Icon]) => (
            <button key={st} onClick={() => setSourceType(st)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                sourceType === st ? 'bg-coral-warm text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
              }`}
            ><Icon className="w-4 h-4" />{label}</button>
          ))}
        </div>

        {sourceType === 'ai_generated' ? (
          <div className="space-y-5">
            {weakConcepts.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recommended for You</p>
                <div className="flex flex-wrap gap-2">
                  {weakConcepts.map(wc => (
                    <button key={wc.id}
                      onClick={() => { setSourceType('ai_generated'); setSelectedConceptId(wc.id); setSelectedConceptName(wc.name); setSelectedSubjectName(wc.subject_name); setDifficulty('medium'); }}
                      className={`px-3 py-2 rounded-xl border text-sm transition-all ${
                        selectedConceptId === wc.id ? 'border-coral-warm bg-orange-50 text-coral-warm font-medium' : 'border-gray-200 hover:border-orange-200 hover:bg-orange-50 text-gray-700'
                      }`}
                    >
                      <span className="font-medium">{wc.name}</span>
                      {wc.subject_name && <span className="text-xs text-gray-400 ml-1">· {wc.subject_name}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {weakConcepts.length > 0 ? 'Or choose manually' : 'Select concept'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Subject</label>
                  <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-coral-warm"
                    value={selectedSubjectId ?? ''}
                    onChange={e => { const id = Number(e.target.value); setSelectedSubjectId(id || null); setSelectedConceptId(null); setSelectedConceptName(''); setSelectedSubjectName(subjects.find(x => x.id === id)?.name ?? ''); }}
                  >
                    <option value="">Select subject…</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Concept</label>
                  <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-coral-warm disabled:bg-gray-50 disabled:text-gray-400"
                    value={selectedConceptId ?? ''} disabled={!selectedSubjectId}
                    onChange={e => { const id = Number(e.target.value); setSelectedConceptId(id || null); setSelectedConceptName(concepts.find(x => x.id === id)?.name ?? ''); }}
                  >
                    <option value="">Select concept…</option>
                    {concepts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paste your study material</label>
            <textarea className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-coral-warm min-h-[160px]"
              placeholder="Paste text…"
              maxLength={25000} value={pasteContent} onChange={e => setPasteContent(e.target.value)}
            />
            <p className="text-xs text-gray-400 text-right">{pasteContent.length}/25,000</p>
          </div>
        )}

        <div className="mt-6 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Difficulty</p>
            <div className="grid grid-cols-3 gap-2">
              {(['easy', 'medium', 'hard'] as Difficulty[]).map(d => (
                <button key={d} onClick={() => setDifficulty(d)}
                  className={`py-2 rounded-xl text-sm font-medium transition-all capitalize border ${
                    difficulty === d ? 'bg-coral-warm text-white border-coral-warm' : 'border-gray-200 text-gray-600 hover:border-orange-200'
                  }`}
                >{d}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Questions</p>
            <div className="grid grid-cols-3 gap-2">
              {[{ n: 10, label: 'Sprint' }, { n: 20, label: 'Challenge' }, { n: 60, label: 'Marathon' }].map(({ n, label }) => (
                <button key={n} onClick={() => setQuestionCount(n as QuestionCount)}
                  className={`py-2 rounded-xl text-sm font-medium transition-all border ${
                    questionCount === n ? 'bg-ocean-deep text-white border-ocean-deep' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                ><span className="font-bold">{n}</span><span className="text-xs block opacity-70">{label}</span></button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Mode</p>
              <div className="grid grid-cols-2 gap-2">
                {([['rapid_fire', 'Rapid-fire'], ['relax', 'Relax']] as [DrillMode, string][]).map(([m, label]) => (
                  <button key={m} onClick={() => setMode(m)}
                    className={`py-2 rounded-xl text-xs font-medium transition-all border ${
                      mode === m ? 'bg-ocean-deep text-white border-ocean-deep' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Timer</p>
              <div className="grid grid-cols-2 gap-2">
                {([2, 5] as (2 | 5)[]).map(t => (
                  <button key={t} onClick={() => setTimerMinutes(t)}
                    className={`py-2 rounded-xl text-xs font-medium transition-all border ${
                      timerMinutes === t ? 'bg-ocean-deep text-white border-ocean-deep' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >{t} min</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {(setupError || generatingError) && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 shrink-0" /> {setupError || generatingError}
          </div>
        )}

        <button onClick={handleStart}
          className="mt-6 w-full py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl font-semibold text-sm shadow-lg shadow-indigo-500/30 transition-all hover:scale-[1.01] flex items-center justify-center gap-2"
        ><Zap className="w-4 h-4" /> Start Drill</button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // GENERATING
  // ─────────────────────────────────────────────────────────────
  if (phase === 'generating') return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-14 h-14 border-4 border-orange-200 border-t-coral-warm rounded-full animate-spin mx-auto" />
        <p className="text-lg font-semibold text-ocean-deep">Preparing your drill…</p>
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // SESSION
  // ─────────────────────────────────────────────────────────────
  if (phase === 'session' && currentQ) return (
    <div className="pb-8 min-h-screen">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm font-medium text-gray-500">Q{currentIndex + 1}<span className="text-gray-300">/{questions.length}</span></span>
          {mode === 'rapid_fire' ? (
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r={RING_R} fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle cx="24" cy="24" r={RING_R} fill="none" stroke={ringColor} strokeWidth="3"
                  strokeDasharray={RING_C} strokeDashoffset={ringOffset} strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 0.1s linear, stroke 0.3s' }}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">{Math.ceil(timeLeft / 1000)}</span>
            </div>
          ) : (
            <div className={`flex items-center gap-1.5 text-sm font-medium ${totalTimeLeft < 30000 ? 'text-red-500' : 'text-gray-500'}`}>
              <Clock className="w-4 h-4" />{formatTime(totalTimeLeft)}
            </div>
          )}
        </div>

        <div className="h-1 bg-gray-100 rounded-full mb-6">
          <div className="h-1 bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full transition-all" style={{ width: `${(currentIndex / questions.length) * 100}%` }} />
        </div>

        <div className="glass-panel rounded-2xl p-5 mb-4">
          <p className="text-base font-medium text-ocean-deep leading-relaxed">{currentQ.question}</p>
        </div>

        <div className="space-y-3 mb-6">
          {OPTION_LABELS.map((label, i) => {
            const optKey = `option_${label.toLowerCase()}` as keyof DrillQuestion;
            return (
              <button key={i} onClick={() => handleAdvance(i)}
                className="w-full flex items-start gap-3 p-4 rounded-2xl border border-gray-200 hover:border-coral-warm hover:bg-orange-50 text-left transition-all group"
              >
                <span className="w-7 h-7 shrink-0 rounded-lg bg-gray-100 group-hover:bg-orange-100 flex items-center justify-center text-xs font-bold text-gray-600 group-hover:text-coral-warm transition-colors">{label}</span>
                <span className="text-sm text-gray-700 leading-relaxed pt-0.5">{currentQ[optKey] as string}</span>
              </button>
            );
          })}
        </div>

        {mode === 'relax' && (
          <button onClick={() => handleAdvance(null, false)}
            className="w-full py-3 border-2 border-gray-200 hover:border-gray-300 rounded-2xl text-sm font-medium text-gray-500 hover:text-gray-700 transition-all flex items-center justify-center gap-2"
          >Skip <ChevronRight className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // RESULTS
  // ─────────────────────────────────────────────────────────────
  if (phase === 'results') return (
    <div className="pb-12 min-h-screen">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex flex-col items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/30">
            <span className="text-3xl font-bold text-white">{pct}%</span>
          </div>
          <h2 className="text-xl font-bold text-ocean-deep">Session Complete</h2>
          <p className="text-gray-500 text-sm mt-1">{correct} correct · {attempted} attempted</p>
        </div>

        {(strengthened.length > 0 || stillWeak.length > 0) && (
          <div className="glass-panel rounded-2xl p-4 mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Concept breakdown</p>
            <div className="flex flex-wrap gap-2">
              {strengthened.map(s => (
                <span key={s} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium capitalize">
                  <CheckCircle2 className="w-3 h-3" />{s}
                </span>
              ))}
              {stillWeak.map(s => (
                <span key={s} className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium capitalize">
                  <AlertTriangle className="w-3 h-3" />{s}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2 mb-8">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Questions</p>
          {results.map((r) => {
            const q = questions[r.question_index];
            if (!q) return null;
            return (
              <div key={r.question_index} className={`rounded-2xl p-4 ${r.correct ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                <div className="flex items-start gap-3">
                  {r.correct ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 leading-relaxed">{q.question}</p>
                    {!r.correct && q.explanation && (
                      <p className="text-xs text-gray-500 mt-1.5 bg-white/60 rounded-lg p-2 leading-relaxed">{q.explanation}</p>
                    )}
                    <ChinniExplainer
                      question={q.question}
                      optionA={q.option_a} optionB={q.option_b}
                      optionC={q.option_c} optionD={q.option_d}
                      correctIndex={q.correct_option_index}
                      conceptName={selectedConceptName}
                      gradeLevel={grade}
                    />
                    {r.timed_out && <p className="text-xs text-amber-600 mt-1">⏱ Time ran out</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={() => { setIdRef.current = ''; setPhase('setup'); }}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
          ><ArrowLeft className="w-4 h-4" /> New Concept</button>
          <button onClick={handleRetake}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
          ><RotateCcw className="w-4 h-4" /> Retake</button>
          <Link to="/drill-archive"
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
          ><BookOpen className="w-4 h-4" /> View History</Link>
          {stillWeak.length > 0 && (
            <button onClick={() => setPhase('setup')}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all"
            >Drill Weak Areas <ChevronRight className="w-4 h-4" /></button>
          )}
        </div>
      </div>
    </div>
  );

  return null;
}
